import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/ui/dashboard-layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormField } from '@/components/ui/form-field';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Checkbox } from '@/components/ui/checkbox';
import { BulkActionBar } from '@/components/ui/bulk-action-bar';
import { useAuthStore } from '@/stores/auth-store';
import { useTickets, useCreateTicket, useBulkDeleteTickets, useBulkUpdateTickets } from '@/hooks/use-tickets';
import { useProjects } from '@/hooks/use-projects';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { useDebounce } from '@/hooks/use-debounce';
import { SkeletonRow } from '@/components/ui/skeleton';
import { toast } from '@/hooks/use-toast';
import { Ticket, Search, MessageSquare, Filter, Plus, Loader2 } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/utils';
import { stripHtml } from '@/components/ui/html-content';
import type { TicketType, TaskPriority } from 'shared';

const ticketTypeLabels: Record<string, string> = {
  bug: 'Bug',
  change_request: 'Change Request',
  general_inquiry: 'General Inquiry',
  approval_request: 'Approval Request',
};

const ticketTypeColors: Record<string, string> = {
  bug: 'bg-red-500/10 text-red-400',
  change_request: 'bg-blue-500/10 text-blue-400',
  general_inquiry: 'bg-purple-500/10 text-purple-400',
  approval_request: 'bg-yellow-500/10 text-yellow-400',
};

const statusColors: Record<string, string> = {
  open: 'bg-green-500/10 text-green-400',
  in_progress: 'bg-blue-500/10 text-blue-400',
  resolved: 'bg-purple-500/10 text-purple-400',
  closed: 'bg-gray-500/10 text-gray-400',
};

const priorityColors: Record<string, string> = {
  low: 'bg-gray-500/10 text-gray-400',
  medium: 'bg-blue-500/10 text-blue-400',
  high: 'bg-orange-500/10 text-orange-400',
  urgent: 'bg-red-500/10 text-red-400',
};

export function TicketsPage() {
  useDocumentTitle('Support Tickets');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isClient = user?.role === 'client';

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Ticket creation dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTicketProject, setNewTicketProject] = useState('');
  const [newTicketSubject, setNewTicketSubject] = useState('');
  const [newTicketType, setNewTicketType] = useState<TicketType>('general_inquiry');
  const [newTicketPriority, setNewTicketPriority] = useState<TaskPriority>('medium');
  const [newTicketDescription, setNewTicketDescription] = useState('');

  const { data: projectsData } = useProjects();
  const projects = projectsData?.data || [];

  const createTicket = useCreateTicket();

  const resetTicketForm = () => {
    setNewTicketProject('');
    setNewTicketSubject('');
    setNewTicketType('general_inquiry');
    setNewTicketPriority('medium');
    setNewTicketDescription('');
  };

  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTicketProject || !newTicketSubject.trim() || !newTicketDescription.trim()) return;

    try {
      await createTicket.mutateAsync({
        projectId: newTicketProject,
        subject: newTicketSubject.trim(),
        type: newTicketType,
        priority: newTicketPriority,
        description: newTicketDescription.trim(),
      });

      toast({
        title: 'Ticket created',
        description: newTicketSubject.trim(),
        variant: 'success',
      });

      resetTicketForm();
      setCreateDialogOpen(false);
    } catch (err: any) {
      toast({
        title: 'Failed to create ticket',
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  const { data: ticketsData, isLoading } = useTickets({
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(typeFilter ? { type: typeFilter } : {}),
  });

  const tickets = (ticketsData?.data || []).filter((t: any) => {
    if (!debouncedSearch) return true;
    const q = debouncedSearch.toLowerCase();
    return (
      t.subject?.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q) ||
      t.submitter?.name?.toLowerCase().includes(q)
    );
  });

  const bulkDeleteTickets = useBulkDeleteTickets();
  const bulkUpdateTickets = useBulkUpdateTickets();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === tickets.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(tickets.map((t: any) => t.id)));
    }
  }, [tickets, selectedIds.size]);

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    try {
      await bulkDeleteTickets.mutateAsync(Array.from(selectedIds));
      toast({
        title: 'Tickets deleted',
        description: `${selectedIds.size} ticket(s) have been deleted.`,
        variant: 'success',
      });
      setSelectedIds(new Set());
    } catch (err: any) {
      toast({
        title: 'Failed to delete tickets',
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  const handleBulkStatusChange = async (status: string) => {
    if (selectedIds.size === 0) return;
    try {
      await bulkUpdateTickets.mutateAsync({
        ids: Array.from(selectedIds),
        updates: { status },
      });
      toast({
        title: 'Tickets updated',
        description: `${selectedIds.size} ticket(s) moved to ${status.replace('_', ' ')}.`,
        variant: 'success',
      });
      setSelectedIds(new Set());
    } catch (err: any) {
      toast({
        title: 'Failed to update tickets',
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  const handleBulkPriorityChange = async (priority: string) => {
    if (selectedIds.size === 0) return;
    try {
      await bulkUpdateTickets.mutateAsync({
        ids: Array.from(selectedIds),
        updates: { priority },
      });
      toast({
        title: 'Tickets updated',
        description: `${selectedIds.size} ticket(s) set to ${priority} priority.`,
        variant: 'success',
      });
      setSelectedIds(new Set());
    } catch (err: any) {
      toast({
        title: 'Failed to update tickets',
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold">Support Tickets</h1>
        <Dialog open={createDialogOpen} onOpenChange={(open) => {
          setCreateDialogOpen(open);
          if (!open) resetTicketForm();
        }}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1.5">
              <Plus size={16} />
              New Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Create New Ticket</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTicket} className="space-y-4">
              {/* Project selector */}
              <FormField label="Project">
                <Select value={newTicketProject} onValueChange={setNewTicketProject}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a project…" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.length === 0 ? (
                      <SelectItem value="_none" disabled>
                        No projects available
                      </SelectItem>
                    ) : (
                      projects.map((p: any) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </FormField>

              {/* Subject */}
              <FormField label="Subject">
                <Input
                  value={newTicketSubject}
                  onChange={(e) => setNewTicketSubject(e.target.value)}
                  placeholder="Brief summary of the issue"
                  required
                />
              </FormField>

              {/* Type + Priority row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <FormField label="Type">
                  <Select value={newTicketType} onValueChange={(v) => setNewTicketType(v as TicketType)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bug">Bug</SelectItem>
                      <SelectItem value="change_request">Change Request</SelectItem>
                      <SelectItem value="general_inquiry">General Inquiry</SelectItem>
                      <SelectItem value="approval_request">Approval Request</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Priority">
                  <Select value={newTicketPriority} onValueChange={(v) => setNewTicketPriority(v as TaskPriority)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
              </div>

              {/* Description */}
              <FormField label="Description">
                <RichTextEditor
                  value={newTicketDescription}
                  onChange={setNewTicketDescription}
                  placeholder="Detailed description of the issue or request"
                  minHeight="120px"
                />
              </FormField>

              <Button
                type="submit"
                className="w-full"
                disabled={createTicket.isPending || !newTicketProject || !newTicketSubject.trim() || !stripHtml(newTicketDescription)}
              >
                {createTicket.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {createTicket.isPending ? 'Creating...' : 'Create Ticket'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6">
        <div className="relative w-full sm:flex-1 sm:min-w-[200px] sm:max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
          <Input
            placeholder="Search tickets..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            aria-label="Search tickets"
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={14} className="text-[var(--text-secondary)] flex-shrink-0" />
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-full sm:w-[140px]" aria-label="Filter by status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>

          <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-full sm:w-[150px]" aria-label="Filter by type">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="bug">Bug</SelectItem>
              <SelectItem value="change_request">Change Request</SelectItem>
              <SelectItem value="general_inquiry">General Inquiry</SelectItem>
              <SelectItem value="approval_request">Approval Request</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tickets list */}
      {isLoading ? (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y divide-[var(--border-color)]">
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={Ticket}
              title="No tickets found"
              description={search || statusFilter || typeFilter
                ? 'Try adjusting your filters or search.'
                : 'Submit a new ticket to get support for your projects.'}
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[var(--text-secondary)]">
              {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* Select all toggle (non-clients only) */}
            {!isClient && (
            <div className="px-4 py-2 border-b border-[var(--border-color)] flex items-center gap-2">
              <Checkbox
                id="select-all-tickets"
                checked={selectedIds.size === tickets.length && tickets.length > 0}
                onCheckedChange={toggleSelectAll}
              />
              <label htmlFor="select-all-tickets" className="text-xs text-[var(--text-secondary)] cursor-pointer select-none">
                {selectedIds.size > 0
                  ? `${selectedIds.size} selected`
                  : 'Select all'}
              </label>
            </div>
            )}
            <div className="divide-y divide-[var(--border-color)]">
              {tickets.map((ticket: any) => (
                <div
                  key={ticket.id}
                  className="flex items-start gap-2 sm:gap-4 p-3 sm:p-4 hover:bg-[var(--bg-secondary)]/50 transition-colors group cursor-pointer"
                  onClick={() => navigate(`/tickets/${ticket.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/tickets/${ticket.id}`); }}
                >
                  {!isClient && (
                  <Checkbox
                    checked={selectedIds.has(ticket.id)}
                    onCheckedChange={() => toggleSelect(ticket.id)}
                    className="flex-shrink-0 mt-1 sm:mt-0.5"
                  />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start sm:items-center gap-1.5 sm:gap-2 mb-1 flex-col sm:flex-row sm:flex-wrap">
                      <span className="text-sm font-medium leading-tight">{ticket.subject}</span>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0', ticketTypeColors[ticket.type])}>
                          {ticketTypeLabels[ticket.type] || ticket.type}
                        </Badge>
                        <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0', statusColors[ticket.status])}>
                          {ticket.status?.replace('_', ' ')}
                        </Badge>
                        <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0', priorityColors[ticket.priority])}>
                          {ticket.priority}
                        </Badge>
                      </div>
                    </div>
                    {ticket.description && (
                      <p className="text-xs text-[var(--text-secondary)] line-clamp-1 mb-1">
                        {stripHtml(ticket.description)}
                      </p>
                    )}
                    <div className="flex items-center gap-2 sm:gap-3 text-[10px] text-[var(--text-secondary)] flex-wrap">
                      <span>#{ticket.id.slice(0, 8)}</span>
                      <span>{ticket.submitter?.name || 'Unknown'}</span>
                      {ticket.assignee && <span>→ {ticket.assignee.name}</span>}
                      {ticket._count?.comments > 0 && (
                        <span className="flex items-center gap-1">
                          <MessageSquare size={10} />
                          {ticket._count.comments}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk action bar (non-clients only) */}
      {!isClient && <BulkActionBar
        selectedCount={selectedIds.size}
        onClearSelection={() => setSelectedIds(new Set())}
        onDelete={handleBulkDelete}
        onStatusChange={handleBulkStatusChange}
        statusOptions={[
          { value: 'open', label: 'Open' },
          { value: 'in_progress', label: 'In Progress' },
          { value: 'resolved', label: 'Resolved' },
          { value: 'closed', label: 'Closed' },
        ]}
        onPriorityChange={handleBulkPriorityChange}
        priorityOptions={[
          { value: 'low', label: 'Low' },
          { value: 'medium', label: 'Medium' },
          { value: 'high', label: 'High' },
          { value: 'urgent', label: 'Urgent' },
        ]}
        isDeleting={bulkDeleteTickets.isPending}
        isUpdatingStatus={bulkUpdateTickets.isPending}
        isUpdatingPriority={bulkUpdateTickets.isPending}
      />
      }
    </DashboardLayout>
  );
}
